import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import { COMPARE_ENABLED } from '@/lib/featureFlags';
import { VIEW_SATISFINDER_PROFILES, hasPermission } from '@/lib/staffPermissions';
import { 
  LayoutDashboard, 
  Building2, 
  QrCode, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  MessageSquare,
  FileBarChart,
  AlertTriangle,
  Layers,
  ClipboardList,
  Crown,
  ArrowLeft,
  ShieldCheck,
  UserSearch,
  FileText,
  CreditCard,
  GitCompare,
  Globe,
  Network
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import WelcomeBanner from '@/components/WelcomeBanner';
import useTenantFeatures from '@/hooks/useTenantFeatures';
import { SubscriptionAccessBanner } from '@/components/subscription/SubscriptionPages';

export default function DashboardLayout({ children }) {
  const { user, logout, impersonating, exitImpersonation, hasGapLibrary, hasCases, hasValidationCodes } = useAuth();
  const tenantFeatures = useTenantFeatures();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clientLogo, setClientLogo] = useState(null);

  useEffect(() => {
    fetch(`${API}/tenant/branding`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.branding?.logo) setClientLogo(d.branding.logo); })
      .catch(() => {});
  }, []);

  const platformNavItems = [
    { path: '/platform', label: 'Platform Dashboard', icon: Crown, roles: ['platform_admin'] },
    { path: '/qa-queue', label: 'QA Queue', icon: ShieldCheck, roles: ['platform_admin', 'qa'] },
    { path: '/qa-admin', label: 'QA Reviewers', icon: Users, roles: ['platform_admin'] },
    { path: '/admin/satisfinders', label: 'satisFINDERs', icon: UserSearch, roles: ['platform_admin'], permission: VIEW_SATISFINDER_PROFILES },
    { path: '/admin/supported-countries', label: 'Supported Countries', icon: Globe, roles: ['platform_admin'] },
    { path: '/admin/pages', label: 'Content / Pages', icon: FileText, roles: ['platform_admin'] },
    { path: '/platform/taxonomy-templates', label: 'Taxonomy Templates', icon: Network, roles: ['platform_admin'] },
  ];

  // Tenant-level nav. `feature` on an item means it's only rendered when the
  // tenant has that feature flag enabled (Platform Admin settings panel).
  // Dashboard, Responses, Branches, Surveys, Users, Settings are always shown.
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff', 'platform_admin'] },
    { path: '/responses', label: 'Responses', icon: MessageSquare, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff', 'platform_admin'], surveyFeature: 'standard' },
    { path: '/reports', label: 'Reports', icon: FileBarChart, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff'], surveyFeature: 'mystery_shopping' },
    // Feb 2026 — Experience Performance. Same access-control envelope as
    // Reports (surveyFeature: mystery_shopping) so it inherits MS product
    // gating without introducing a new feature flag.
    { path: '/experience-performance', label: 'Experience Performance', icon: FileBarChart, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff'], surveyFeature: 'mystery_shopping' },
    { path: '/compare', label: 'Compare', icon: GitCompare, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff'], surveyFeature: 'mystery_shopping' },
    { path: '/cases', label: 'Cases', icon: AlertTriangle, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff', 'platform_admin'], feature: 'cases' },
    { path: '/branches', label: 'Branches', icon: Building2, roles: ['tenant_admin', 'platform_admin'] },
    { path: '/codes', label: 'Validation Codes', icon: QrCode, roles: ['tenant_admin', 'platform_admin'], feature: 'validation_codes' },
    { path: '/surveys', label: 'Surveys', icon: ClipboardList, roles: ['tenant_admin', 'platform_admin'] },
    { path: '/gaps', label: 'Gap Library', icon: Layers, roles: ['tenant_admin', 'platform_admin'], feature: 'gap_library' },
    { path: '/taxonomy', label: 'Attributes & Factors', icon: Network, roles: ['tenant_admin', 'super_admin', 'platform_admin'], surveyFeature: 'standard' },
    { path: '/users', label: 'Users', icon: Users, roles: ['tenant_admin', 'platform_admin'] },
    { path: '/admin/subscription', label: 'Subscription', icon: CreditCard, roles: ['tenant_admin'] },
    { path: '/settings', label: 'Settings', icon: Settings, roles: ['tenant_admin', 'platform_admin'] },
  ];

  const isPlatformAdmin = user?.role === 'platform_admin';
  const isImpersonating = isPlatformAdmin && impersonating?.impersonating;

  const isFeatureEnabled = (key) => {
    if (!key) return true;
    if (key === 'cases') return hasCases;
    if (key === 'validation_codes') return hasValidationCodes;
    if (key === 'gap_library') return hasGapLibrary;
    return false;
  };

  // Fix 2 — Survey-type gating for /responses and /reports.
  // Platform admin (when NOT impersonating) bypasses; the impersonated
  // tenant's features still drive what shows under impersonation.
  // Until the features call resolves we keep the legacy behaviour (show both)
  // by treating `_loaded === false` as "show everything" to avoid a flash
  // where a tenant's working sidebar disappears.
  const isSurveyFeatureEnabled = (kind) => {
    if (!kind) return true;
    if (user?.role === 'platform_admin' && !isImpersonating) return true;
    if (!tenantFeatures._loaded) return true;  // pre-resolution: don't hide
    if (kind === 'standard') return !!tenantFeatures.has_standard_surveys;
    if (kind === 'mystery_shopping') return !!tenantFeatures.has_mystery_shopping;
    return true;
  };

  // Platform admin without impersonation only sees platform nav.
  // Platform admin with impersonation sees platform nav + the gated tenant nav.
  const filteredPlatformNav = platformNavItems.filter(item =>
    item.roles.includes(user?.role || '') || (item.permission && hasPermission(user, item.permission)));
  const filteredNavItems = (!isPlatformAdmin || isImpersonating)
    ? navItems.filter(item =>
        item.roles.includes(user?.role || 'branch_manager')
        && (item.path !== '/compare' || COMPARE_ENABLED)  // temporarily hidden
        && isFeatureEnabled(item.feature)
        && isSurveyFeatureEnabled(item.surveyFeature))
    : [];

  const getRoleBadge = (role) => {
    const badges = {
      platform_admin: { label: 'Platform', color: 'bg-[#7C3AED] text-white' },
      qa: { label: 'QA', color: 'bg-[#0EA5E9] text-white' },
      client: { label: 'Client', color: 'bg-[#6366F1] text-white' },
      tenant_admin: { label: 'Admin', color: 'bg-[#F1D511] text-[#0A0F1B]' },
      regional_manager: { label: 'Regional', color: 'bg-[#5BC4BF] text-white' },
      branch_manager: { label: 'Branch', color: 'bg-[#4EBC87] text-white' }
    };
    return badges[role] || badges.branch_manager;
  };

  const roleBadge = getRoleBadge(user?.role);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E2E8F0] px-4 py-3">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex items-center gap-2">
            {clientLogo ? (
              <img src={clientLogo} alt="Logo" className="h-8 rounded" />
            ) : (
              <img
                src="/satisfind-logo.png"
                alt="satisFIND"
                className="h-8"
                data-testid="dashboard-default-logo-mobile"
              />
            )}
            <span className="font-['Montserrat'] font-bold text-[#0F172A]">VOX</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-[#1A3E6F] text-white text-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={logout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Sidebar */}
      <aside 
        style={{ backgroundColor: '#1A3E6F' }}
        className={`fixed left-0 top-0 z-50 w-[260px] h-screen flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        data-testid="dashboard-sidebar"
      >
        <div className="p-6 flex-1 min-h-0 overflow-y-auto">
          {/* Logo */}
          <div className="flex flex-col items-start gap-2 mb-8">
            {clientLogo ? (
              <img src={clientLogo} alt="Logo" className="h-10 rounded-lg" />
            ) : (
              <img
                src="https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/7w3grvpa_satisFIND_20th_Logo_white_transparent.png"
                alt="satisFIND"
                style={{ width: 160, height: 'auto' }}
                data-testid="dashboard-default-logo"
              />
            )}
            <div className="flex items-center gap-1">
              <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>powered by satisFIND&reg;</span>
            </div>
          </div>

          {/* Nav */}
          <nav className="space-y-1">
            {/* Platform nav (platform_admin only) */}
            {filteredPlatformNav.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all"
                  style={{
                    color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.7)',
                    backgroundColor: isActive ? 'rgba(124,58,237,0.3)' : 'transparent',
                    borderLeft: isActive ? '3px solid #A78BFA' : '3px solid transparent',
                  }}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}

            {/* Impersonation banner */}
            {isImpersonating && (
              <div className="mx-2 my-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <p className="text-[11px] font-medium" style={{ color: '#FCD34D' }}>Viewing as</p>
                <p className="text-sm font-semibold text-white truncate">{impersonating.tenant_name}</p>
                <button
                  onClick={() => { exitImpersonation(); setSidebarOpen(false); }}
                  className="flex items-center gap-1.5 mt-2 text-xs font-medium hover:underline"
                  style={{ color: '#FCD34D' }}
                  data-testid="exit-impersonation-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to Platform
                </button>
              </div>
            )}

            {/* Tenant section separator */}
            {isPlatformAdmin && isImpersonating && filteredNavItems.length > 0 && (
              <div className="px-4 pt-2 pb-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Tenant</p>
              </div>
            )}

            {/* Tenant nav items */}
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all"
                  style={{
                    color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.7)',
                    backgroundColor: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                    borderLeft: isActive ? '3px solid #F1D511' : '3px solid transparent',
                  }}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* User Info at bottom */}
        <div className="shrink-0 p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors"
                style={{ color: 'white' }}
                data-testid="user-menu-btn"
              >
                <Avatar className="w-10 h-10">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${roleBadge.color}`}>
                    {roleBadge.label}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-[#64748B] truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => window.location.href = '/settings'}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="md:ml-[260px] p-6 pt-20 md:pt-6 min-h-screen">
        <WelcomeBanner />
        <SubscriptionAccessBanner />
        {children}
      </main>
    </div>
  );
}
